import { Link, useLocation } from 'react-router-dom'

const TABS = [
  { to: '/settings/tokens', label: 'Jira credentials' },
  { to: '/settings/mcp-tokens', label: 'MCP tokens' },
]

/** Jira credentials ⇄ MCP tokens segmented control in the Settings page header. */
export function SettingsTabs() {
  const { pathname } = useLocation()
  return (
    <div className="inline-flex shrink-0 rounded-md border border-line-strong" role="tablist" aria-label="Settings sections">
      {TABS.map((t, i) => {
        const active = pathname === t.to
        return (
          <Link
            key={t.to}
            to={t.to}
            role="tab"
            aria-selected={active}
            className={[
              'px-3 py-1 text-[12px] transition-colors',
              i > 0 ? 'border-l border-line-strong' : '',
              active ? 'bg-surface-active text-foreground' : 'text-text-2 hover:bg-surface-raised hover:text-foreground',
            ].join(' ')}
          >
            {t.label}
          </Link>
        )
      })}
    </div>
  )
}

/** Labelled input for settings forms (label above, mono value). */
export function SettingsField({ id, label, type = 'text', placeholder, value, onChange, required = true }: {
  id: string
  label: string
  type?: string
  placeholder?: string
  value: string
  onChange: (v: string) => void
  required?: boolean
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block font-mono text-[11px] text-text-3">{label}</label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        // Autofill is dangerous here: the browser fills base_url / organization_id from its own
        // record and the value is saved on submit. Secrets are never shown back, so a wrong
        // base_url only surfaces later as broken Jira calls. "new-password" is the only value
        // that reliably stops password managers on secret fields.
        autoComplete={type === 'password' ? 'new-password' : 'off'}
        className="h-8 w-full rounded-md border border-input bg-surface px-2.5 font-mono text-[12.5px] text-foreground placeholder:text-text-3"
      />
    </div>
  )
}

/** Result line of a settings action: ✓ ok / ✗ error, the word carries the meaning. */
export function SettingsStatus({ msg }: { msg: string }) {
  const isError = msg.startsWith('Error')
  return (
    <p role={isError ? 'alert' : 'status'} className={`font-mono text-[12px] ${isError ? 'text-danger' : 'text-ok'}`}>
      {isError ? '✗' : '✓'} {msg}
    </p>
  )
}

/** Primary submit of a settings form (the one accent action of the pane). */
export const settingsPrimaryClass =
  'inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1 text-[12px] font-semibold text-accent-fg transition-colors hover:bg-primary/90 disabled:opacity-60'
